/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';
import { cssMap, jsx } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import type { SuggestionsEmptyStateVariant } from './SuggestionsEmptyState.tsx';

const styles = cssMap({
	container: {
		display: 'grid',
		// A fixed middle row anchors the illustration independently of the text height.
		gridTemplateRows: 'minmax(0, 1fr) 240px minmax(0, 1fr)',
		boxSizing: 'border-box',
		minHeight: '100%',
		paddingTop: token('space.500'),
		paddingBottom: token('space.300'),
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: token('space.200'),
		width: '100%',
		gridRow: 2,
		alignSelf: 'start',
	},
	illustration: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '56px',
		flexShrink: 0,
	},
	background: {
		position: 'absolute',
		inset: 0,
		zIndex: -1,
		pointerEvents: 'none',
		backgroundColor: token('elevation.surface.raised'),
		overflow: 'hidden',
	},
	glowEllipse: {
		position: 'absolute',
		width: '240px',
		height: '176px',
		borderRadius: '50%',
		filter: 'blur(80px)',
	},
	topGlow: {
		top: '-128px',
		left: 0,
	},
	bottomGlow: {
		bottom: '-128px',
		right: 0,
	},
	grid: {
		position: 'absolute',
		inset: 0,
		opacity: 0.4,
		// Two repeating dot strips form 24px cells, with 1px dots spaced every 3px.
		// A 0.5px centre places each dot's edges on whole CSS pixels.
		backgroundImage: `radial-gradient(circle at 0.5px 0.5px, ${token('color.background.accent.gray.subtle')} 0.5px, transparent 0.5px), radial-gradient(circle at 0.5px 0.5px, ${token('color.background.accent.gray.subtle')} 0.5px, transparent 0.5px)`,
		backgroundSize: '24px 3px, 3px 24px',
		// Start with a full cell at the top; any partial row is clipped at the bottom.
		backgroundPosition: '0 0',
		backgroundRepeat: 'repeat',
	},
	gridWash: {
		position: 'absolute',
		inset: 0,
		backgroundColor: token('color.background.accent.gray.subtlest'),
		opacity: 0.2,
	},
	initial: {
		backgroundColor: token('color.background.accent.purple.subtler'),
	},
	loading: {
		backgroundColor: token('color.background.accent.yellow.subtlest'),
	},
	allResolved: {
		backgroundColor: token('color.background.accent.lime.subtlest'),
	},
	noSuggestionsResult: {
		backgroundColor: token('color.background.accent.gray.subtler'),
	},
});

export const SuggestionsGridBackground = ({
	variant,
	illustration,
	children,
	testId,
}: {
	variant: SuggestionsEmptyStateVariant | 'loading';
	illustration?: React.ReactNode;
	children: React.ReactNode;
	testId?: string;
}): React.JSX.Element => {
	return (
		<div css={styles.container} data-testid={testId}>
			<div aria-hidden css={styles.background}>
				<div css={styles.grid} />
				<div css={styles.gridWash} />
				<div css={[styles.glowEllipse, styles.topGlow, styles[variant]]} />
				<div css={[styles.glowEllipse, styles.bottomGlow, styles[variant]]} />
			</div>
			<div css={styles.content}>
				{illustration && <div css={styles.illustration}>{illustration}</div>}
				{children}
			</div>
		</div>
	);
};
